import { navigate } from 'gatsby';
import { useEffect, useState, useContext } from 'react';
import { Auth } from 'aws-amplify';
import { useApolloClient } from '@apollo/react-hooks';
import ROUTES from '../routes';
import { sendNewTrustedDeviceAddedEmailMutation } from '../queries/trustedDeviceEmail.query';
import { parseDeviceName } from '../util/parseDeviceName';
import {
  getFromStorage,
  setInStorage,
  removeFromStorage,
} from '../util/storage-utils';
import useTransition from './useTransition';
import { useTranslation } from './useTranslation';
import useFormState from './useFormState';
import useAuth from './useAuth';
import {
  AUTH_ERROR,
  AuthError,
  createAuthError,
  AuthErrorCode,
} from '../lib/AuthenticationService/errorCodes';
import config from '../config';
import { SignInWithPasswordMfaRequired } from '../lib/AuthenticationService/types';

import validationRules, {
  Model,
  MFAModel,
} from '../components/sign-in-form/SignInForm.rules';
import usePrevious from './usePrevious';
import { featureFlagsContext } from '../providers/FeatureFlagsProvider';
import { StormModeInterceptContext } from '../providers/StormModeInterceptProvider';
import { NotificationsContext } from '../providers/NotificationsProvider';

// keeping these exports outside of the useSignInForm hook since it will be used for the useRegistrationForm() hook as well
export const isValidPassword = (password: string): boolean =>
  new RegExp(
    '^(?=.*[A-Za-zd$?@?$?!?%?#???&?)])(?=.*[a-z])(?=.*[A-Z])(?=.*[0-9]){8,16}',
  ).test(password);

export interface FormData {
  [key: string]: string;
}

const handleRememberMe = (rememberMe: boolean, email: string): void => {
  if (rememberMe) {
    setInStorage('userEmail', email);
  } else {
    removeFromStorage('userEmail');
  }
};

const DEFAULT_AUTH_FLOW = config.cognito.authenticationFlowType;

/**
 * Returns true if this browser has a Cognito deviceKey stored for the user.
 *
 * Used to select USER_SRP_AUTH vs USER_PASSWORD_AUTH during Firebase → Cognito migration.
 * After migration is complete, this check and getAuthFlowType can be removed and USER_SRP_AUTH can be used
 *  for all authentication attempts.
 */
const hasCognitoDeviceKey = (email: string): boolean => {
  if (typeof window === 'undefined') {
    return false;
  }

  const storage = window.localStorage;
  if (!storage) {
    return false;
  }

  const normalizedEmail = email.trim().toLowerCase();
  if (!normalizedEmail) {
    return false;
  }

  const clientId = config.cognito.userPoolWebClientId;
  if (!clientId) {
    return false;
  }

  const key = `CognitoIdentityServiceProvider.${clientId}.${normalizedEmail}.deviceKey`;

  return storage.getItem(key) !== null;
};

const getAuthFlowType = (
  email: string,
  enableTrustedDevice: boolean,
): string | undefined => {
  if (enableTrustedDevice && hasCognitoDeviceKey(email)) {
    return 'USER_SRP_AUTH';
  }
  return DEFAULT_AUTH_FLOW;
};

export default (props: {
  redirectUrl?: string;
  newEmail?: string | undefined;
  previousEmail?: string | undefined;
  closeModal?: () => void;
}) => {
  const authService = useAuth();
  const apolloClient = useApolloClient();
  const { webTurnOnTrustedDevice } = useContext(featureFlagsContext);
  const { isOutageMode, isShowOutageModeModal } = useContext(
    featureFlagsContext,
  );
  const notificationContext = useContext(NotificationsContext);
  const { t } = useTranslation();
  const { redirectUrl, newEmail, previousEmail, closeModal } = props;
  const onSuccessUrl = redirectUrl || ROUTES.ACCOUNT;
  // const onSuccessUrl = redirectUrl || defaultRedirectRoute;
  const [invalidPasswordCount, setPasswordInvalidCount] = useState(0);
  const [isTwoStepRequired, setIsTwoStepRequired] = useState<boolean>(false);
  const { setStormModeInterceptModal } = useContext(StormModeInterceptContext);

  const emailFromStorage: string | undefined = getFromStorage('userEmail');
  let defaultEmail = emailFromStorage;

  if (newEmail) {
    defaultEmail = newEmail;
  }

  if (previousEmail) {
    defaultEmail = previousEmail;
  }
  const defaultPersistLogin = Boolean(getFromStorage('persistLogin'));
  const form = useFormState(
    {
      email: defaultEmail || '',
      password: '',
      rememberMe: true,
      persistLogin: defaultPersistLogin,
    },
    {
      validate: validationRules,
      validationContext: {
        t,
      },
    },
  );
  const twoStepVerificationForm = useFormState({
    mfaCode: '',
    phoneHint: '',
    trustThisDevice: false,
  });
  const { hideTransition, showTransition } = useTransition();

  const rememberDevice = async (trustThisDevice: boolean) => {
    if (!trustThisDevice) {
      return;
    }
    try {
      // After confirmSignIn, Amplify v5 triggers an internal ConfirmDevice
      // callback asynchronously. We need to wait for that to complete before
      // calling rememberDevice(), otherwise UpdateDeviceStatus fails silently
      // because the device key isn't confirmed yet.
      await new Promise(resolve => setTimeout(resolve, 1000));

      // Refresh the authenticated user to ensure the device key is populated
      const user = await Auth.currentAuthenticatedUser({ bypassCache: true });

      // Verify the device key exists before attempting to remember
      const deviceKey =
        user?.deviceKey ?? user?.signInUserSession?.deviceKey ?? null;

      if (deviceKey) {
        await Auth.rememberDevice();
        const devices = (await Auth.fetchDevices()) as
          | { id: string; name?: string | null }[]
          | undefined;

        const currentDevice = devices?.find(d => d.id === deviceKey);
        const deviceName = parseDeviceName(currentDevice?.name);
        if (deviceName) {
          apolloClient
            .mutate({
              mutation: sendNewTrustedDeviceAddedEmailMutation,
              variables: {
                payload: {
                  deviceName,
                },
              },
            })
            .catch(() => undefined);
        }
      }
    } catch (error) {
      // Remembering the device is best-effort; surface failure for visibility.
      console.error('Failed to remember trusted device', error);
      notificationContext?.setState?.({
        isOpen: true,
        severity: 'error',
        message: t('GENERIC_ERROR_NOTIFICATION_MESSAGE_BODY'),
      });
    }
  };

  const applyCognitoMfaChallenge = (
    challenge: SignInWithPasswordMfaRequired,
  ) => {
    const phoneHint =
      challenge.challengeParameters.CODE_DELIVERY_DESTINATION ||
      challenge.challengeParameters.CODE_DELIVERY_DELIVERY_MEDIUM ||
      '';
    void twoStepVerificationForm.setValue('phoneHint', phoneHint);
    void twoStepVerificationForm.setValue('mfaCode', '');
    void twoStepVerificationForm.setValue('trustThisDevice', false);
    twoStepVerificationForm.setError('mfaCode', null);
    setIsTwoStepRequired(true);
    hideTransition();
  };

  const navigateForgotPassword = (showNotification = false) => {
    handleRememberMe(form.values.rememberMe, form.values.email);

    // eslint-disable-next-line @typescript-eslint/no-floating-promises
    navigate(ROUTES.FORGOT_PASSWORD, {
      state: {
        redirectUrl,
        showNotification,
      },
    });
  };

  useEffect(() => {
    if (invalidPasswordCount > 2) {
      navigateForgotPassword(true);
      closeModal && void closeModal();
    }
  }, [invalidPasswordCount]);
  const values = form.values;
  const previousValues = usePrevious(values);

  useEffect(() => {
    if (previousValues !== null) {
      if (previousValues.rememberMe !== form.values.rememberMe) {
        handleRememberMe(form.values.rememberMe, values.email);
      }
    }
  });

  const showErrorMessage = (code: AuthErrorCode): void => {
    switch (code) {
      case AUTH_ERROR.EMAIL_NOT_FOUND:
        form.setError('email', t('ERROR_AUTH_INVALID_DEFAULT'));
        break;
      case AUTH_ERROR.INVALID_PASSWORD:
        form.setError('password', t('ERROR_AUTH_INVALID_DEFAULT'));
        break;
      case AUTH_ERROR.TOO_MANY_ATTEMPTS:
        form.setError('password', t('ERROR_AUTH_EXCEEDED_ATTEMPTS'));
        break;
      case AUTH_ERROR.USER_NOT_CONFIRMED:
        form.setError('email', t('ERROR_AUTH_USER_NOT_CONFIRMED'));
        break;
      case AUTH_ERROR.PASSWORD_RESET_REQUIRED:
        form.setError('password', t('ERROR_AUTH_PASSWORD_RESET_REQUIRED'));
        break;
      case AUTH_ERROR.USER_DISABLED:
        form.setError('email', t('ERROR_AUTH_USER_DISABLED'));
        break;
      case AUTH_ERROR.INVALID_CODE:
        twoStepVerificationForm.setError(
          'mfaCode',
          t('TWO_STEP_LOGIN_ERROR_INVALID_CODE'),
        );
        break;
      case AUTH_ERROR.CODE_EXPIRED:
        twoStepVerificationForm.setError(
          'mfaCode',
          t('TWO_STEP_LOGIN_ERROR_CODE_EXPIRED'),
        );
        break;
      default:
        handleBackToLogin();
        form.setError('password', t('ERROR_AUTH_UNEXPECTED'));
    }
  };

  const handleAuthErrors = (err: unknown) => {
    const authError = err instanceof AuthError ? err : createAuthError(err);
    showErrorMessage(authError.code);
    setPasswordInvalidCount(previous => previous + 1);
    hideTransition();
  };

  const onLoginSuccess = (url: string, state = {}) => {
    hideTransition();
    if (isShowOutageModeModal) {
      setStormModeInterceptModal({
        open: true,
        defaultRedirectUrl: url,
        locationState: state,
      });
      return;
    } else {
      return navigate(url, { replace: true, state });
    }
  };

  const handleLogin = async (data: Model) => {
    showTransition(t('SUBMITTING_REQUEST'));

    try {
      handleRememberMe(Boolean(data.rememberMe), data.email);
      const authFlowType = getAuthFlowType(data.email, webTurnOnTrustedDevice);
      if (authFlowType) {
        Auth.configure({ authenticationFlowType: authFlowType });
      }
      const result = await authService.signInWithPassword(
        data.email,
        data.password,
      );

      if (result.status === 'SUCCESS') {
        return onLoginSuccess(onSuccessUrl);
      }

      if (result.status === 'MFA_REQUIRED') {
        applyCognitoMfaChallenge(result);
        return;
      }
      return handleAuthErrors(new Error('Unexpected sign-in result'));
    } catch (err) {
      return handleAuthErrors(err as Error);
    }
  };

  const handleLoginWithTwoStep = async (data: MFAModel) => {
    if (!isTwoStepRequired) {
      twoStepVerificationForm.setError('mfaCode', t('ERROR_AUTH_UNEXPECTED'));
      return;
    }
    showTransition(t('SUBMITTING_REQUEST'));

    try {
      await authService.signInWithMultiFactorAuth(data.mfaCode);

      await rememberDevice(webTurnOnTrustedDevice && data.trustThisDevice);
      setIsTwoStepRequired(false);
      return onLoginSuccess(onSuccessUrl);
    } catch (err) {
      const authError = err instanceof AuthError ? err : createAuthError(err);
      showErrorMessage(authError.code);
      hideTransition();
    }
  };

  const handleBackToLogin = () => {
    setIsTwoStepRequired(false);
    void twoStepVerificationForm.setValue('mfaCode', '');
    void twoStepVerificationForm.setValue('trustThisDevice', false);
    twoStepVerificationForm.setError('mfaCode', null);
    authService.clearLastSignInCredentials?.();
  };

  const handleResendOneTimeCode = async (
    clearCachedCredentialsOnFailure = false,
  ) => {
    if (!isTwoStepRequired) {
      return;
    }

    showTransition(t('SUBMITTING_REQUEST'));
    try {
      const refreshedChallenge = await authService.resendMfaCode({
        clearCachedCredentialsOnFailure,
      });
      if (refreshedChallenge && refreshedChallenge.status === 'MFA_REQUIRED') {
        applyCognitoMfaChallenge(refreshedChallenge);
      }
    } catch (_error) {
      twoStepVerificationForm.setError('mfaCode', t('ERROR_AUTH_UNEXPECTED'));
    } finally {
      hideTransition();
    }
  };

  const handleSubmit = form.submit(handleLogin);

  const handleTwoFactorSubmit = twoStepVerificationForm.submit(
    handleLoginWithTwoStep,
  );

  return {
    isTwoStepRequired,
    handleSubmit,
    handleTwoFactorSubmit,
    handleResendOneTimeCode,
    handleBackToLogin,
    form,
    twoStepVerificationForm,
    handleForgotPassword: navigateForgotPassword,
    invalidPasswordCount,
  };
};
